<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // CASSIOPEIA
    import {
        BrandTags,
        BrandSelectMultiple,
        Button,
        CategoryTags,
        Heading,
        OrganizationForm,
        Paragraph,
        SelectChannels,
        SelectMarkets,
        SelectOrganizationTypes,
        SESSION_ORGANIZATION,
        SubscriptionForm,
        openModal,
        SESSION_USER,
        InputToggle,
        BrandSimilarTags,
    } from '@smatch/cassiopeia-web-components';

    // STYLING
    import 'tailwindcss/tailwind.css';

    // TYPES
    import type { SvelteComponent } from 'svelte';
    import type { Subscription } from 'src/app/types';
    import type { Writable } from 'svelte/store';

    /* TYPES ----------------------------------------------------------------------------------- */

    interface Form extends SvelteComponent {
        submit(): Promise<void>;
    }

    /* INTERNALS ------------------------------------------------------------------------------- */

    let organizationForm: Form | null = null;
    let subscriptionForm: Form | null = null;

    $: SUBSCRIPTION = subscriptionForm === null ? undefined : subscriptionForm.DATA as Writable<Subscription>;

    const ORGANIZATION = { channelCodes: [], marketCodes: [], types: [], ...$SESSION_ORGANIZATION };

    $: hasTypes = $SESSION_ORGANIZATION !== null
        && Array.isArray($SESSION_ORGANIZATION.types)
        && $SESSION_ORGANIZATION.types.length > 0;

    $: hasChannels = $SESSION_ORGANIZATION !== null
        && Array.isArray($SESSION_ORGANIZATION.channelCodes)
        && $SESSION_ORGANIZATION.channelCodes.length > 0;

    $: hasMarketCodes = $SESSION_ORGANIZATION !== null
        && Array.isArray($SESSION_ORGANIZATION.marketCodes)
        && $SESSION_ORGANIZATION.marketCodes.length > 0;

    $: isOnlyWholesaler = Array.isArray(ORGANIZATION.types)
        && ORGANIZATION.types.includes('buyer_wholesaler')
        && ORGANIZATION.types.length === 1;

    $: hasSelectedTypes = Array.isArray(ORGANIZATION.types)
        && ORGANIZATION.types.length > 0;

    const onSave = async (): Promise<void> => {
        await (organizationForm as OrganizationForm).submit();
        openModal('#onboarding');
    };

    const beforeSave = (): void => {
        if ($SUBSCRIPTION !== undefined && $SESSION_USER !== null) {
            $SUBSCRIPTION.ownerKey = $SESSION_USER._.key;
        }
    };

    const ALLOWS_SIMILAR_BRANDS = $SUBSCRIPTION?.constraints.allowsSimilarBrands ?? false;
    let isSimilarBrandsChecked = ALLOWS_SIMILAR_BRANDS;


    $: if ($SUBSCRIPTION?.constraints.brandKeys?.length === 0) {
        isSimilarBrandsChecked = false;
        $SUBSCRIPTION.constraints.allowsSimilarBrands = false;
    }

    $: if ($SUBSCRIPTION !== undefined) {
        $SUBSCRIPTION.constraints.allowsSimilarBrands = isSimilarBrandsChecked;
    }

</script>

<div class = 's8-wrap'>

    <SubscriptionForm action = 'create' on:save = { onSave } { beforeSave } bind:this = { subscriptionForm }>

        <Heading size = 'md'>Sourcing Preferences</Heading>

        <OrganizationForm action = 'update' organization = { ORGANIZATION } bind:this = { organizationForm }>

            {#if !hasTypes}
                <SelectOrganizationTypes
                    bind:keys = { ORGANIZATION.types }
                    label = { null }
                    placeholder = 'What best describes your business?'
                    domain = 'buyer'
                />
            {/if}

            {#if hasSelectedTypes && !isOnlyWholesaler}

                {#if !hasChannels }
                    <SelectChannels
                        label = 'On which channels do you sell?'
                        bind:keys = { ORGANIZATION.channelCodes }
                    />
                {/if}

                {#if !hasMarketCodes}
                    <SelectMarkets
                        label = 'On which markets do you sell?'
                        bind:keys = { ORGANIZATION.marketCodes }
                        minItems = { 1 }
                    />
                {/if}

            {/if}

        </OrganizationForm>

        {#if $SUBSCRIPTION !== undefined}

            <Paragraph>Which fashion categories are you interested in?</Paragraph>
            <CategoryTags
                bind:keys = { $SUBSCRIPTION.constraints.categoryKeys }
                label = { null }
                verticalCode = { $SUBSCRIPTION.constraints.verticalCode }
            />

            {#if isSimilarBrandsChecked}
                {#key $SUBSCRIPTION.constraints.brandKeys?.length}
                    <BrandSelectMultiple
                        bind:keys = { $SUBSCRIPTION.constraints.brandKeys }
                        verticalCode = { $SUBSCRIPTION.constraints.verticalCode }
                        placeholder = 'Adidas, Nike, Hoka, etc.'
                    />
                {/key}
                <BrandTags
                    bind:keys = { $SUBSCRIPTION.constraints.brandKeys }
                    parameters = { { isRecommended: true, verticalCodes: [ $SUBSCRIPTION.constraints.verticalCode ] } }
                    label = 'What about?'
                />
            {:else}
                <BrandSelectMultiple
                    bind:keys = { $SUBSCRIPTION.constraints.brandKeys }
                    placeholder = 'Which brands are you interested in?'
                    verticalCode = { $SUBSCRIPTION.constraints.verticalCode }
                    label = { null }
                />
                <BrandTags
                    bind:keys = { $SUBSCRIPTION.constraints.brandKeys }
                    parameters = { { isRecommended: true, verticalCodes: [ $SUBSCRIPTION.constraints.verticalCode ] } }
                    label = 'What about?'
                />
            {/if}

            <InputToggle
                bind:isChecked={ isSimilarBrandsChecked }
                label = 'Receive notification pertaining to related brands.'
                name = 'subscription.allowsSimilarBrands'
                css = 'mb-4'
            />
            {#if isSimilarBrandsChecked}
                <BrandSimilarTags
                    bind:keys = { $SUBSCRIPTION.constraints.brandKeys }
                    parameters = {{ similarToBrandKeys: $SUBSCRIPTION.constraints.brandKeys }}
                />
            {/if}

        {/if}

        <div class = 's8-footer'>
            <Button
                type = 'button'
                label = 'Cancel'
                color = 'neutral'
                css = 'w-full'
                url = '#onboarding'
            />

            <Button
                type = 'submit'
                label = 'Save'
                css = 'w-full'
            />
        </div>

    </SubscriptionForm>

</div>

<style lang='postcss'>

    div.s8-wrap {

        div.s8-footer {
            display: flex;
            column-gap: 10px;
            flex-grow: 1;
            align-items: flex-end;
        }

        :global(form#s8-form-bulk-create-subscription) {
            flex-grow: 1;
            display: flex;
        }

        :global(form#s8-form-bulk-create-subscription div.s8-form-body) {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
        }
    }

</style>